import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeft, 
  Play, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Target,
  Zap,
  BookOpen,
  Trophy,
  Timer,
  ArrowRight,
  Home
} from 'lucide-react';
import { 
  coursesApi, 
  practiceApi,
  CoursePracticeModesResponse,
  PracticeModeInfo,
  PracticeSession,
  PracticeQuestion,
  PracticeResults,
  StartPracticeRequest
} from '../services/api';
import { MixedText } from '../utils/rtl';
import { Card, CardHeader, CardTitle, CardContent, CardFooter, SpaceCard } from './ui/Card';
import { Button } from './ui/Button';
import { DashboardContainer } from './layout/PageContainer';
import StudyMaterialRenderer from './StudyMaterialRenderer';


const CoursePractice: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const { courseId: courseIdParam } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  
  // Parse courseId and handle undefined case
  const courseId = courseIdParam ? parseInt(courseIdParam, 10) : 0;
  
  const [loading, setLoading] = useState(true);
  const [practiceMode, setPracticeMode] = useState<'selection' | 'practice' | 'results'>('selection');
  const [modesData, setModesData] = useState<CoursePracticeModesResponse | null>(null);
  const [practiceSession, setPracticeSession] = useState<PracticeSession | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<PracticeQuestion | null>(null);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [results, setResults] = useState<PracticeResults | null>(null);
  const [answering, setAnswering] = useState(false);

  useEffect(() => {
    if (courseId && courseId > 0) {
      fetchPracticeModesAndStart();
    } else {
      setLoading(false);
    }
  }, [courseId]);

  const fetchPracticeModesAndStart = async () => {
    try {
      const data = await coursesApi.getPracticeModes(courseId);
      setModesData(data);
      
      // Auto-start quick practice if available
      const quickMode = data.available_modes.find(mode => mode.mode === 'quick');
      if (quickMode && quickMode.is_available) {
        await startPracticeSession('quick');
      }
    } catch (error) {
      console.error('Error fetching practice modes:', error);
    } finally {
      setLoading(false);
    }
  };

  const startPracticeSession = async (mode: string) => {
    try {
      const session = await coursesApi.startPracticeSession(courseId, { practice_mode: mode });
      setPracticeSession(session);
      setPracticeMode('practice');
      await fetchCurrentQuestion(session.id);
    } catch (error) {
      console.error('Error starting practice session:', error);
    }
  };

  const fetchCurrentQuestion = async (sessionId: number) => {
    try {
      const question = await practiceApi.getCurrentQuestion(sessionId);
      setCurrentQuestion(question);
      setSelectedOption(null);
    } catch (error) {
      console.error('Error fetching question:', error);
    }
  };

  const submitAnswer = async () => {
    if (!practiceSession || !currentQuestion || selectedOption === null) return;
    
    setAnswering(true);
    try {
      const result = await practiceApi.submitAnswer(practiceSession.id, {
        practice_session_id: practiceSession.id,
        question_id: currentQuestion.question.id,
        selected_option_id: selectedOption
      });
      
      // Update practice session state
      setPracticeSession(prev => prev ? {
        ...prev,
        current_question_index: result.current_question_index,
        correct_answers: result.correct_answers
      } : null);
      
      // Check if practice is complete
      if (result.practice_session_completed) {
        await fetchResults(practiceSession.id);
      } else {
        // Fetch next question after a brief delay
        setTimeout(() => {
          fetchCurrentQuestion(practiceSession.id);
        }, 1500);
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
    } finally {
      setAnswering(false);
    }
  };

  const fetchResults = async (sessionId: number) => {
    try {
      const resultsData = await practiceApi.getResults(sessionId);
      setResults(resultsData);
      setPracticeMode('results');
    } catch (error) {
      console.error('Error fetching results:', error);
    }
  };

  const getModeIcon = (mode: string) => {
    switch (mode) {
      case 'quick': return <Zap className="w-8 h-8" />;
      case 'session_focus': return <BookOpen className="w-8 h-8" />;
      case 'mixed_exam': return <Target className="w-8 h-8" />;
      case 'lightning': return <Timer className="w-8 h-8" />;
      default: return <Play className="w-8 h-8" />;
    }
  };

  const getModeColor = (mode: string) => {
    switch (mode) {
      case 'quick': return 'from-vibrant-orange to-vibrant-orange/80';
      case 'session_focus': return 'from-muted-purple to-muted-purple/80';
      case 'mixed_exam': return 'from-deep-space-purple to-muted-purple';
      case 'lightning': return 'from-vibrant-orange/80 to-muted-purple/80';
      default: return 'from-muted-purple to-muted-purple/80';
    }
  };

  if (loading) {
    return (
      <DashboardContainer>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vibrant-orange"></div>
        </div>
      </DashboardContainer>
    );
  }

  if (!courseId || courseId <= 0) {
    return (
      <DashboardContainer>
        <div className="text-center py-16">
          <h1 className="text-2xl font-bold text-surface-primary mb-4">🌌 Invalid Course</h1>
          <p className="text-surface-secondary mb-6">The course you're looking for doesn't exist in our galaxy.</p>
          <Button
            onClick={() => navigate('/')}
            variant="primary"
            size="lg"
          >
            {t('common:actions.back_to_dashboard')}
          </Button>
        </div>
      </DashboardContainer>
    );
  }

  // Mode Selection Screen
  if (practiceMode === 'selection') {
    return (
      <DashboardContainer>
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(`/course/${courseId}`)}
              className={`flex items-center text-surface-secondary hover:text-vibrant-orange transition-colors mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              {isRTL ? (
                <ArrowRight className="w-5 h-5 ml-2" />
              ) : (
                <ArrowLeft className="w-5 h-5 mr-2" />
              )}
              <MixedText>{t('common:actions.back_to_course')}</MixedText>
            </button>
            
            <SpaceCard constellation="orion" className="p-6 mb-8">
              <CardHeader>
                <CardTitle className={`text-3xl font-bold mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  ⚡ {t('practice:course_practice.title')}
                </CardTitle>
                <CardContent className={`text-surface-secondary mb-4 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  {t('practice:course_practice.subtitle')}
                </CardContent>
                
                {modesData && (
                  <div className={`flex items-center text-sm text-surface-muted ${isRTL ? 'justify-end space-x-reverse space-x-4' : 'space-x-4'}`}>
                    <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <CheckCircle className={`w-4 h-4 text-muted-purple ${isRTL ? 'ml-1' : 'mr-1'}`} />
                      <MixedText>
                        {t('practice:course_practice.sessions_completed', { completed: modesData.completed_sessions, total: modesData.total_sessions })}
                      </MixedText>
                    </span>
                    <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <Target className={`w-4 h-4 text-vibrant-orange ${isRTL ? 'ml-1' : 'mr-1'}`} />
                      <MixedText>
                        {t('practice:course_practice.questions_available', { count: modesData.total_questions })}
                      </MixedText>
                    </span>
                  </div>
                )}
              </CardHeader>
            </SpaceCard>
          </div>

          {/* Practice Modes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {modesData?.available_modes.map((mode) => (
              <SpaceCard 
                key={mode.mode} 
                constellation="cassiopeia"
                className={`transition-all duration-300 overflow-hidden ${
                  mode.is_available 
                    ? 'hover:shadow-xl hover:scale-[1.02] cursor-pointer' 
                    : 'opacity-60'
                }`}
              >
                {/* Mode Icon Header */}
                <CardHeader className={`bg-gradient-to-r ${getModeColor(mode.mode)} p-6 text-astronaut-white relative overflow-hidden`}>
                  {/* Cosmic glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 transform -skew-x-12" />
                  
                  <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''} relative z-10`}>
                    <div className="flex-shrink-0">
                      {getModeIcon(mode.mode)}
                    </div>
                    <div className={`${isRTL ? 'mr-4 text-right' : 'ml-4'}`}>
                      <CardTitle className="text-xl font-bold text-astronaut-white mb-1">
                        {mode.name}
                      </CardTitle>
                      <div className={`flex items-center text-astronaut-white/80 text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Clock className={`w-4 h-4 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                        <span>{mode.estimated_time_minutes} {t('practice:course_practice.min')}</span>
                        <span className="mx-2">•</span>
                        <span>{mode.question_count} {t('common:labels.questions')}</span>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                {/* Mode Content */}
                <CardContent className="p-6">
                  <p className={`text-surface-secondary mb-4 leading-relaxed ${isRTL ? 'text-right' : ''}`}>
                    {mode.description}
                  </p>
                  
                  {mode.is_available ? (
                    <Button
                      onClick={() => startPracticeSession(mode.mode)}
                      variant="primary"
                      size="lg"
                      className="w-full"
                      style={{
                        background: `linear-gradient(135deg, ${getModeColor(mode.mode).includes('vibrant-orange') ? '#F76700' : '#7B5D99'}, ${getModeColor(mode.mode).includes('vibrant-orange') ? '#FF8533' : '#9B7BB8'})`,
                        boxShadow: `0 8px 24px ${getModeColor(mode.mode).includes('vibrant-orange') ? 'rgba(247, 103, 0, 0.25)' : 'rgba(123, 93, 153, 0.25)'}, inset 0 1px 0 rgba(255, 255, 255, 0.2)`
                      }}
                    >
                      <Play className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      🚀 {t('practice:course_practice.start_practice')}
                    </Button>
                  ) : (
                    <div className={`text-center ${isRTL ? 'text-right' : ''}`}>
                      <p className="text-surface-muted text-sm mb-2">{mode.requirements}</p>
                      <Button
                        disabled
                        variant="secondary"
                        size="lg"
                        className="w-full opacity-50 cursor-not-allowed"
                      >
                        🔒 {t('practice:course_practice.not_available_yet')}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </SpaceCard>
            ))}
          </div>
        </div>
      </DashboardContainer>
    );
  }

  // Practice Session Screen
  if (practiceMode === 'practice' && currentQuestion && practiceSession) {
    const progress = (practiceSession.current_question_index / practiceSession.total_questions) * 100;
    
    return (
      <DashboardContainer>
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          {/* Back Button */}
          <button
            onClick={() => navigate(`/course/${courseId}`)}
            className={`flex items-center text-surface-secondary hover:text-vibrant-orange transition-colors mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            {isRTL ? (
              <ArrowRight className="w-5 h-5 ml-2" />
            ) : (
              <ArrowLeft className="w-5 h-5 mr-2" />
            )}
            <MixedText>{t('common:actions.back_to_dashboard')}</MixedText>
          </button>

          {/* Progress Header */}
          <SpaceCard constellation="ursa" className="p-6 mb-8">
            <CardHeader>
              <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex-1 ${isRTL ? 'text-right' : ''}`}>
                  <CardTitle className={`text-2xl font-bold font-hebrew mb-2 ${isRTL ? 'text-right' : ''}`}>
                    🎯 <MixedText>
                      {t('practice:course_practice.questionOf', { 
                        current: currentQuestion.question_number, 
                        total: currentQuestion.total_questions 
                      })}
                    </MixedText>
                  </CardTitle>
                  <CardContent className={`text-surface-secondary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    <MixedText>{currentQuestion.session_title}</MixedText>
                  </CardContent>
                </div>
                <div className={`text-center ${isRTL ? 'mr-6' : 'ml-6'}`}>
                  <div className={`text-sm text-surface-muted mb-1 font-hebrew`}>
                    <MixedText>{t('practice:progress')}</MixedText>
                  </div>
                  <div className="text-2xl font-bold text-vibrant-orange">{Math.round(progress)}%</div>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-surface-muted/20 rounded-full h-3 overflow-hidden">
                <div 
                  className={`bg-gradient-to-r from-vibrant-orange to-muted-purple h-3 rounded-full transition-all duration-500`}
                  style={{ 
                    width: `${progress}%`,
                    float: isRTL ? 'right' : 'left'
                  }}
                />
              </div>
            </CardHeader>
          </SpaceCard>

          {/* Question */}
          <SpaceCard constellation="cassiopeia" className="p-8 mb-8">
            <CardHeader>
              <div className={`mb-6 ${isRTL ? 'text-right' : ''}`}>
                <StudyMaterialRenderer 
                  content={currentQuestion.question.question_text}
                  materialType="question"
                  isRTL={isRTL}
                />
              </div>

              {/* Options */}
              <div className="space-y-3">
                {currentQuestion.question.options.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setSelectedOption(option.id)}
                    className={`
                      w-full p-4 rounded-xl border-2 transition-all duration-300 ${isRTL ? 'text-right' : 'text-left'}
                      hover:scale-[1.02] hover:shadow-lg
                      ${selectedOption === option.id
                        ? 'border-vibrant-orange bg-vibrant-orange/10 text-surface-primary shadow-lg shadow-vibrant-orange/20'
                        : 'border-surface-muted/30 bg-surface-muted/5 hover:border-vibrant-orange/50 hover:bg-vibrant-orange/5'
                      }
                    `}
                  >
                    <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className={`
                        w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-300
                        ${selectedOption === option.id
                          ? 'border-vibrant-orange bg-vibrant-orange shadow-sm'
                          : 'border-surface-muted hover:border-vibrant-orange'
                        } ${isRTL ? 'ml-3' : 'mr-3'}
                      `}>
                        {selectedOption === option.id && (
                          <div className="w-2 h-2 bg-astronaut-white rounded-full" />
                        )}
                      </div>
                      <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                        <StudyMaterialRenderer 
                          content={option.option_text}
                          materialType="answer_option"
                          isRTL={isRTL}
                        />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </CardHeader>
          </SpaceCard>

          {/* Submit Button */}
          <div className="text-center">
            <Button
              onClick={submitAnswer}
              disabled={selectedOption === null || answering}
              variant={selectedOption !== null ? "primary" : "secondary"}
              size="lg"
              className={`px-8 py-4 text-lg ${selectedOption === null ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {answering ? (
                <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`animate-spin rounded-full h-5 w-5 border-b-2 border-astronaut-white ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                  <MixedText>{t('practice:course_practice.submitting')}</MixedText>
                </div>
              ) : (
                <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  ✨ <MixedText>{t('common:actions.submit_answer')}</MixedText>
                  {isRTL ? (
                    <ArrowLeft className="w-5 h-5 mr-2" />
                  ) : (
                    <ArrowRight className="w-5 h-5 ml-2" />
                  )}
                </div>
              )}
            </Button>
          </div>
        </div>
      </DashboardContainer>
    );
  }

  // Results Screen
  if (practiceMode === 'results' && results) {
    const scoreColor = results.score_percentage >= 85 ? 'text-muted-purple' : 
                     results.score_percentage >= 70 ? 'text-vibrant-orange' : 'text-vibrant-orange/70';
    
    const getScoreEmoji = () => {
      if (results.score_percentage >= 85) return '🏆';
      if (results.score_percentage >= 70) return '⭐';
      return '🚀';
    };
    
    return (
      <DashboardContainer>
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          {/* Results Header */}
          <div className="text-center mb-8">
            <SpaceCard constellation="orion" className="p-8 relative overflow-hidden">
              {/* Celebratory background effect */}
              <div className="absolute inset-0 -z-10">
                <div className="absolute top-0 left-1/4 w-32 h-32 bg-gradient-to-br from-vibrant-orange/10 to-muted-purple/10 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-0 right-1/4 w-24 h-24 bg-gradient-to-br from-muted-purple/10 to-vibrant-orange/10 rounded-full blur-2xl animate-pulse delay-1000"></div>
              </div>
              
              <CardHeader className="text-center">
                <Trophy className={`w-16 h-16 mx-auto mb-4 ${scoreColor} transition-all duration-500 hover:scale-110`} />
                <CardTitle className={`text-3xl font-bold mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  {getScoreEmoji()} {t('practice:course_practice.practice_complete')}
                </CardTitle>
                <div className={`text-6xl font-bold mb-4 ${scoreColor} transition-all duration-500`}>
                  {results.score_percentage}%
                </div>
                <CardContent className={`text-surface-secondary ${isRTL ? 'text-right' : ''}`}>
                  ✨ You got {results.correct_answers} out of {results.total_questions} questions correct
                </CardContent>
              </CardHeader>
            </SpaceCard>
          </div>

          {/* Results Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Performance */}
            <SpaceCard constellation="cassiopeia" className="p-6">
              <CardHeader>
                <CardTitle className={`text-xl font-semibold mb-4 ${isRTL ? 'text-right' : ''}`}>
                  📊 Performance
                </CardTitle>
                <div className="space-y-3">
                  <div className={`flex ${isRTL ? 'flex-row-reverse' : ''} justify-between`}>
                    <span className="text-surface-secondary">✅ Correct Answers:</span>
                    <span className="font-semibold text-muted-purple">{results.correct_answers}</span>
                  </div>
                  <div className={`flex ${isRTL ? 'flex-row-reverse' : ''} justify-between`}>
                    <span className="text-surface-secondary">❌ Incorrect:</span>
                    <span className="font-semibold text-vibrant-orange/70">{results.total_questions - results.correct_answers}</span>
                  </div>
                  <div className={`flex ${isRTL ? 'flex-row-reverse' : ''} justify-between`}>
                    <span className="text-surface-secondary">🎯 Accuracy:</span>
                    <span className={`font-semibold ${scoreColor}`}>{results.score_percentage}%</span>
                  </div>
                </div>
              </CardHeader>
            </SpaceCard>

            {/* Improvement Areas */}
            {results.weak_sessions.length > 0 && (
              <SpaceCard constellation="ursa" className="p-6">
                <CardHeader>
                  <CardTitle className={`text-xl font-semibold mb-4 ${isRTL ? 'text-right' : ''}`}>
                    🔍 Review Needed
                  </CardTitle>
                  <div className="space-y-2">
                    {results.weak_sessions.map((session, index) => (
                      <div key={index} className={`flex items-center text-surface-secondary ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <XCircle className={`w-4 h-4 text-vibrant-orange/70 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        <span className={isRTL ? 'text-right' : ''}>{session}</span>
                      </div>
                    ))}
                  </div>
                </CardHeader>
              </SpaceCard>
            )}
          </div>

          {/* Wrong Answers Review */}
          {results.wrong_answers && results.wrong_answers.length > 0 && (
            <SpaceCard constellation="orion" className="p-6 mb-8">
              <CardHeader>
                <CardTitle className={`text-xl font-semibold mb-4 ${isRTL ? 'text-right' : ''}`}>
                  📚 Review Incorrect Answers
                </CardTitle>
                <CardContent className={`text-surface-secondary mb-6 ${isRTL ? 'text-right' : ''}`}>
                  Here are the questions you got wrong with the correct answers:
                </CardContent>
              </CardHeader>
              
              <CardContent>
                <div className="space-y-6">
                  {results.wrong_answers.map((wrongAnswer, index) => (
                    <div key={index} className="border border-vibrant-orange/30 rounded-xl p-6 bg-vibrant-orange/5">
                      {/* Session Context */}
                      <div className={`text-sm text-surface-muted mb-2 ${isRTL ? 'text-right' : ''}`}>
                        📍 From: {wrongAnswer.session_title}
                      </div>
                      
                      {/* Question */}
                      <div className={`mb-4 ${isRTL ? 'text-right' : ''}`}>
                        <StudyMaterialRenderer 
                          content={wrongAnswer.question.question_text}
                          materialType="question"
                          isRTL={isRTL}
                        />
                      </div>
                      
                      {/* Your Answer */}
                      {wrongAnswer.selected_option && (
                        <div className="mb-4 p-4 bg-vibrant-orange/10 border border-vibrant-orange/30 rounded-lg">
                          <div className={`flex items-center text-vibrant-orange mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <XCircle className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span className="font-semibold">❌ Your Answer:</span>
                          </div>
                          <div className={`${isRTL ? 'text-right' : ''}`}>
                            <StudyMaterialRenderer 
                              content={wrongAnswer.selected_option.option_text}
                              materialType="answer_option"
                              isRTL={isRTL}
                            />
                          </div>
                        </div>
                      )}
                      
                      {/* Correct Answer */}
                      <div className="p-4 bg-muted-purple/10 border border-muted-purple/30 rounded-lg">
                        <div className={`flex items-center text-muted-purple mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <CheckCircle className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                          <span className="font-semibold">✅ Correct Answer:</span>
                        </div>
                        <div className={`${isRTL ? 'text-right' : ''}`}>
                          <StudyMaterialRenderer 
                            content={wrongAnswer.correct_option.option_text}
                            materialType="answer_option"
                            isRTL={isRTL}
                          />
                        </div>
                        
                        {/* Explanation if available */}
                        {wrongAnswer.question.explanation && (
                          <div className="mt-3 pt-3 border-t border-muted-purple/20">
                            <p className={`text-sm text-muted-purple font-medium mb-1 ${isRTL ? 'text-right' : ''}`}>💡 Explanation:</p>
                            <div className={`${isRTL ? 'text-right' : ''}`}>
                              <StudyMaterialRenderer 
                                content={wrongAnswer.question.explanation}
                                materialType="explanation"
                                isRTL={isRTL}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </SpaceCard>
          )}

          {/* Suggestions */}
          <SpaceCard constellation="ursa" className="p-6 mb-8">
            <CardHeader>
              <CardTitle className={`text-xl font-semibold mb-4 ${isRTL ? 'text-right' : ''}`}>
                🚀 Next Steps
              </CardTitle>
              <div className="space-y-3">
                {results.improvement_suggestions.map((suggestion, index) => (
                  <div key={index} className={`flex items-start text-surface-secondary ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <CheckCircle className={`w-5 h-5 text-muted-purple ${isRTL ? 'ml-3' : 'mr-3'} mt-0.5 flex-shrink-0`} />
                    <span className={isRTL ? 'text-right' : ''}>{suggestion}</span>
                  </div>
                ))}
              </div>
            </CardHeader>
          </SpaceCard>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => {
                setResults(null);
                setPracticeSession(null);
                setCurrentQuestion(null);
                setPracticeMode('selection');
              }}
              variant="primary"
              size="lg"
              className="px-6 py-3"
            >
              🚀 Practice Again
            </Button>
            <Button
              onClick={() => navigate(`/course/${courseId}`)}
              variant="secondary"
              size="lg"
              className="px-6 py-3"
            >
              <Home className="w-5 h-5 mr-2 inline" />
              🏠 Back to Course
            </Button>
          </div>
        </div>
      </DashboardContainer>
    );
  }

  return null;
};

export default CoursePractice; 